<script lang="ts" setup>
import dayjs from 'dayjs';

import FilesBaseTable from './FilesBaseTable.vue';
import FilesTableSearch from './Search/index.vue';
import { useQueryFilterFiles } from './Search/useQueryFilterFiles';

import NoDataUrql from '@/ui/NoDataUrql/NoDataUrql.vue';
import Skeletons from '@/ui/Skeletons.vue';
import type { FileFragmentFragment, FilesQueryVariables } from '@/graphql/generated/graphql';
import { useFilesQuery } from '@/graphql/generated/graphql';
import type { TableSearchParameters } from '@/ui/Table/tableProperties';

const props = withDefaults(defineProps<{
  getLink?: (data: FileFragmentFragment) => string;
  onClick?: (data: FileFragmentFragment) => void;
  onSelect?: (data: FileFragmentFragment) => void;
  setUrl?: boolean;
}>(), {
  setUrl: true,
});

const {
  filters,
  state,
  orderBy,
  setTableQuery,
} = useQueryFilterFiles({ syncUrl: props.setUrl });

const { data, fetching, error, executeQuery } = useFilesQuery({
  variables: computed<FilesQueryVariables>(() => ({
    page: filters.value.page,
    orderBy: orderBy.value ? [orderBy.value] : undefined,
    inputGenerated: {
      id: filters.value.id,
      idOperator: filters.value.idOperator,
      name: filters.value.name,
      nameOperator: filters.value.nameOperator,
      originalName: filters.value.originalName,
      originalNameOperator: filters.value.originalNameOperator,
      mimeType: filters.value.mimeType,
      mimeTypeOperator: filters.value.mimeTypeOperator,
      ext: filters.value.ext,
      extOperator: filters.value.extOperator,
      path: filters.value.path,
      pathOperator: filters.value.pathOperator,
      uri: filters.value.uri,
      uriOperator: filters.value.uriOperator,
      size: filters.value.size,
      sizeOperator: filters.value.sizeOperator,
      creationTime: filters.value.creationTime ? dayjs(filters.value.creationTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
      creationTimeOperator: filters.value.creationTimeOperator,
      lastUpdateTime: filters.value.lastUpdateTime ? dayjs(filters.value.lastUpdateTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
      lastUpdateTimeOperator: filters.value.lastUpdateTimeOperator,
      createdBy: filters.value.createdBy,
      createdByOperator: filters.value.createdByOperator,
      lastUpdateBy: filters.value.lastUpdateBy,
      lastUpdateByOperator: filters.value.lastUpdateByOperator,
      lastUpdateByType: filters.value.lastUpdateByType,
      lastUpdateByTypeOperator: filters.value.lastUpdateByTypeOperator,
      lastUpdateReason: filters.value.lastUpdateReason,
      lastUpdateReasonOperator: filters.value.lastUpdateReasonOperator,
    },
  })),
});

const filesData = computed(() => data.value?.files);

const tableQuery = computed<TableSearchParameters>(() => ({
  page: filters.value.page ?? 1,
  orderBy: orderBy.value
    ? [{ column: orderBy.value.column, order: orderBy.value.order }]
    : undefined,
}));

const refetch = () => executeQuery({ requestPolicy: 'network-only' });
</script>

<template>
  <div class="flex flex-col gap-4">
    <FilesTableSearch v-model:values="state" />
    <NoDataUrql
      v-if="!filesData?.data"
      :error="error"
      :fetching="fetching"
    >
      <Skeletons :total="10" :avatar="false" :paragraph="false" />
    </NoDataUrql>
    <FilesBaseTable
      v-if="filesData?.data"
      :pagination="filesData.paginatorInfo"
      :data-source="filesData.data"
      :loading="fetching"
      :get-link="getLink"
      :on-click="onClick"
      :on-select="onSelect"
      :refetch="refetch"
      :query="tableQuery"
      :set-query="setTableQuery"
      hide-search
    />
  </div>
</template>
